import { Container, Play, MonitorPlay, Square } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import type { ContainerSpec } from "@ide/shared/desktop";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/overlay";
import { Field, Input, Spinner } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { activeWorkspace } from "@/workspace/file-ops";
import {
  activeRun,
  readPlanFiles,
  startContainer,
  startLive,
  stopRun,
  useLocalRuns,
  type RunProject,
} from "./local-runs";
import { detectPlans, type RunPlan } from "./plan";
import { EngineCard, openRunLogs, useRunsUi } from "./runs-ui";

type Choice = { id: "live" } | { id: "plan"; plan: RunPlan };

/** "Rodar na sua máquina": Live Server ou contêiner (Compose, Dockerfile ou imagem detectada). */
export function RunLocalDialog() {
  const project = useRunsUi((s) => s.dialog);
  return (
    <Dialog open={Boolean(project)} onOpenChange={(v) => !v && useRunsUi.setState({ dialog: null })}>
      {project ? (
        <DialogContent
          title="Rodar na sua máquina"
          description={`${project.name} · só você vê e controla; o time continua editando o mesmo código.`}
        >
          <RunLocalBody key={project.id} project={project} />
        </DialogContent>
      ) : null}
    </Dialog>
  );
}

const portValue = (v: string) => {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= 65535 ? n : null;
};

function RunLocalBody({ project }: { project: RunProject }) {
  const runs = useLocalRuns((s) => s.runs);
  const engine = useLocalRuns((s) => s.engine);
  const [options, setOptions] = useState<Choice[] | null>(null);
  const [selected, setSelected] = useState(0);
  const [spec, setSpec] = useState<ContainerSpec | null>(null);
  const [busy, setBusy] = useState(false);
  const live = activeRun(runs, project.id, "live");
  const container = activeRun(runs, project.id, "container");

  useEffect(() => {
    const ws = activeWorkspace.current;
    let alive = true;
    const files = ws && ws.detail.project.id === project.id ? readPlanFiles(ws.session) : Promise.resolve({});
    void files.then((f: Record<string, string>) => {
      if (!alive) return;
      const paths = Object.keys(f);
      const site = paths.some((p) => p === "index.html" || p.endsWith("/index.html"));
      const plans = detectPlans(f).map((plan): Choice => ({ id: "plan", plan }));
      // Site sem backend (sem package.json, Compose ou Dockerfile): o Live Server vem primeiro.
      const siteFirst = site && plans[0]?.id === "plan" && plans[0].plan.spec.image === "nginx:alpine";
      const all: Choice[] = site
        ? siteFirst
          ? [{ id: "live" }, ...plans]
          : [...plans, { id: "live" }]
        : plans;
      setOptions(all);
      setSelected(0);
      const first = all[0];
      setSpec(first?.id === "plan" ? { ...first.plan.spec } : null);
    });
    return () => {
      alive = false;
    };
  }, [project.id]);

  const choose = (i: number) => {
    setSelected(i);
    const c = options?.[i];
    setSpec(c?.id === "plan" ? { ...c.plan.spec } : null);
  };

  const choice = options?.[selected] ?? null;
  const isContainer = choice?.id === "plan";
  const ready = engine?.state === "ready";
  const close = () => useRunsUi.setState({ dialog: null });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!choice) return;
    setBusy(true);
    try {
      if (choice.id === "live") {
        if (await startLive(project)) close();
      } else if (spec) {
        const run = await startContainer(project, spec);
        if (run) {
          close();
          openRunLogs(run.id);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  if (!options) {
    return (
      <div className="flex items-center gap-2 py-6 text-[13px] text-fg-muted">
        <Spinner className="h-4 w-4" /> Lendo o projeto para ver como rodar…
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div role="radiogroup" aria-label="Como rodar" className="space-y-1.5">
        {options.map((c, i) => {
          const on = i === selected;
          const title = c.id === "live" ? "Pré-visualizar (servidor local)" : c.plan.title;
          const summary =
            c.id === "live"
              ? "Serve o site (HTML, CSS, JS) em localhost e recarrega a cada alteração — sua ou do time."
              : c.plan.summary;
          const running = c.id === "live" ? live : container;
          const Icon = c.id === "live" ? MonitorPlay : Container;
          return (
            <button
              key={c.id === "live" ? "live" : `${c.plan.id}-${i}`}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => choose(i)}
              className={cn(
                "flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors",
                on
                  ? "border-fg/50 bg-bg-muted"
                  : "border-border hover:border-border-strong hover:bg-bg-muted/60",
              )}
            >
              <span
                className={cn(
                  "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
                  on ? "border-fg" : "border-fg-subtle",
                )}
              >
                {on ? <span className="h-2 w-2 rounded-full bg-fg" /> : null}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 text-[13px] font-medium text-fg">
                  <Icon className="h-3.5 w-3.5 text-fg-muted" /> {title}
                  {i === 0 ? (
                    <span className="text-[11px] font-normal text-fg-subtle">· recomendado</span>
                  ) : null}
                  {running ? <span className="text-[11px] font-normal text-fg-subtle">· rodando</span> : null}
                </span>
                <span className="mt-0.5 block text-[12px] leading-relaxed text-fg-muted">{summary}</span>
              </span>
            </button>
          );
        })}
      </div>

      {isContainer ? <EngineCard /> : null}

      {isContainer && spec && ready ? (
        <div className="space-y-3">
          {spec.mode === "image" ? (
            <>
              <Field
                label="Imagem"
                hint="Do Docker Hub (ex.: node:22-alpine, python:3.13-slim) ou de outro registro."
              >
                <Input
                  value={spec.image ?? ""}
                  onChange={(e) => setSpec({ ...spec, image: e.target.value.trim() })}
                  spellCheck={false}
                  className="font-mono text-[12px]"
                />
              </Field>
              <Field
                label="Comando"
                hint="Roda dentro do contêiner, na pasta do projeto. Em branco: o da imagem."
              >
                <Input
                  value={spec.command ?? ""}
                  onChange={(e) => setSpec({ ...spec, command: e.target.value })}
                  spellCheck={false}
                  className="font-mono text-[12px]"
                />
              </Field>
            </>
          ) : null}
          {spec.mode !== "compose" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Porta do app" hint="Onde o app escuta no contêiner.">
                <Input
                  inputMode="numeric"
                  value={spec.containerPort ?? ""}
                  onChange={(e) => setSpec({ ...spec, containerPort: portValue(e.target.value) })}
                  className="font-mono text-[12px]"
                />
              </Field>
              <Field label="Porta em localhost" hint="Em branco: a mesma, ou a próxima livre.">
                <Input
                  inputMode="numeric"
                  value={spec.hostPort ?? ""}
                  placeholder="automática"
                  onChange={(e) => setSpec({ ...spec, hostPort: portValue(e.target.value) })}
                  className="font-mono text-[12px]"
                />
              </Field>
            </div>
          ) : (
            <p className="text-[12px] leading-relaxed text-fg-muted">
              As portas vêm do arquivo do Compose
              {spec.ports?.length ? ` (${spec.ports.map((p) => `localhost:${p}`).join(", ")})` : ""}.
            </p>
          )}
        </div>
      ) : null}

      {isContainer && container ? (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-bg px-3 py-2 text-[12px] text-fg-muted">
          <span className="flex-1">Este projeto já está rodando num contêiner.</span>
          <Button size="sm" variant="ghost" type="button" onClick={() => openRunLogs(container.id)}>
            Saída
          </Button>
          <Button size="sm" variant="secondary" type="button" onClick={() => void stopRun(container.id)}>
            <Square className="h-3 w-3" /> Parar
          </Button>
        </div>
      ) : null}

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={close}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={busy}
          disabled={!choice || (isContainer && (!ready || Boolean(container) || !spec))}
        >
          <Play className="h-3.5 w-3.5 fill-current" />
          {choice?.id === "live" && live ? "Abrir no navegador" : "Rodar"}
        </Button>
      </DialogFooter>
    </form>
  );
}
